import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  useTheme,
  TablePagination,
} from "@mui/material";
import {
  CreditCardOutlined,
  AddOutlined,
  CheckCircleOutline,
  RefreshOutlined,
  PauseCircleOutline,
  BlockOutlined,
  VerifiedUserOutlined,
  TrendingUpOutlined,
  AccountBalanceOutlined,
  InfoOutlined,
  SendOutlined,
  LayersOutlined,
  MoneyOffOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import Swal from "sweetalert2";
import { formatKES } from "../WalletManagement/WalletBalance";
import {
  walletQuery,
  walletMutate,
  REQUEST_OVERDRAFT_FACILITY_MUTATION,
  APPROVE_OVERDRAFT_FACILITY_MUTATION,
  SUSPEND_OVERDRAFT_FACILITY_MUTATION,
  CLOSE_OVERDRAFT_FACILITY_MUTATION,
  OVERDRAFT_FACILITY_QUERY,
  OVERDRAFT_FACILITIES_QUERY,
  OVERDRAFT_FACILITY_REQUESTS_QUERY,
  REQUEST_APPROVAL_OTP_MUTATION,
  REQUEST_OVERDRAFT_MAKER_OTP_MUTATION,
  VALIDATE_OVERDRAFT_MAKER_OTP_MUTATION,
} from "../../data/WalletService/walletGraphQL";
import { executeAnyMutation, fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { walletGraphqlUrl, companyManagementUrl } from "../../config";
import { loadUsersMap, getUserDisplayName, getMaskedUserEmail } from "./userResolver";

const OverdraftFacilityManagement = ({ tenants = [] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // User Display Names Map
  const [userMap, setUserMap] = useState(new Map());

  useEffect(() => {
    let mounted = true;
    loadUsersMap().then((map) => {
      if (mounted && map) setUserMap(new Map(map));
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Tenant List (filters and shows all active companies and tenants like MCB Switch)
  const [tenantList, setTenantList] = useState([]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetchJsonDynamic(
          "companies",
          null,
          null,
          companyManagementUrl.uri
        );

        let companies = [];
        if (response?.results) {
          try {
            companies = JSON.parse(response.results) || [];
          } catch (e) {
            console.error("Error parsing company data:", e);
          }
        } else if (Array.isArray(response)) {
          companies = response;
        }

        // Filter active companies and sort by most recent (highest Id first)
        const activeCompanies = Array.isArray(companies)
          ? companies
            .filter((c) => c?.IsActive || c?.isActive)
            .sort((a, b) => (b?.Id || b?.id || 0) - (a?.Id || a?.id || 0))
          : [];

        const mapped = activeCompanies.map((c) => ({
          id: c.Id ?? c.id,
          name: c.CompanyName ?? c.companyName ?? c.Name ?? c.name ?? `Company #${c.Id ?? c.id}`,
          isActive: true,
        }));

        setTenantList(mapped);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  // Also reconcile if parent passes active tenants prop
  useEffect(() => {
    if (Array.isArray(tenants) && tenants.length > 0) {
      const active = tenants
        .filter((t) => t?.isActive !== false && t?.IsActive !== false)
        .map((t) => ({
          id: t.id ?? t.companyId ?? t.Id,
          name: t.name ?? t.companyName ?? t.CompanyName ?? `Tenant #${t.id ?? t.companyId ?? t.Id}`,
          isActive: true,
        }))
        .sort((a, b) => (b?.id || 0) - (a?.id || 0));
      if (active.length > 0) {
        setTenantList(active);
      }
    }
  }, [tenants]);

  const displayTenants = tenantList;

  const getCompanyName = useCallback(
    (companyId) => {
      if (!companyId && companyId !== 0) return "-";
      const cid = String(companyId);
      const match =
        displayTenants.find((t) => String(t.id ?? t.companyId) === cid) ||
        (Array.isArray(tenants) &&
          tenants.find((t) => String(t.id ?? t.companyId) === cid));
      return match ? (match.name ?? match.companyName) : `Company #${companyId}`;
    },
    [displayTenants, tenants]
  );

  // Filter & Data States
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [facilities, setFacilities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination States
  const [requestsPage, setRequestsPage] = useState(0);
  const [requestsRowsPerPage, setRequestsRowsPerPage] = useState(5);
  const [facilitiesPage, setFacilitiesPage] = useState(0);
  const [facilitiesRowsPerPage, setFacilitiesRowsPerPage] = useState(5);

  // Request Overdraft Modal State
  const [requestOpen, setRequestOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [targetCompanyId, setTargetCompanyId] = useState("");
  const [requestedLimit, setRequestedLimit] = useState("500000");
  const [annualRate, setAnnualRate] = useState("12.0");
  const [investorShare, setInvestorShare] = useState("80.0");
  const [investorReference, setInvestorReference] = useState("");
  const [notes, setNotes] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const cid = selectedCompanyId ? parseInt(selectedCompanyId, 10) : null;
      const statusFilter = selectedStatus !== "ALL" ? selectedStatus : null;

      // 1. Fetch Facilities (batch with seamless single-tenant fallback)
      let loadedFacilities = [];
      try {
        const facRes = await walletQuery(OVERDRAFT_FACILITIES_QUERY, { companyId: cid });
        if (Array.isArray(facRes?.overdraftFacilities)) {
          loadedFacilities = facRes.overdraftFacilities;
        }
      } catch (facErr) {
        console.warn("[OverdraftFacility] Batch query failed, attempting single/fan-out fallback:", facErr.message);
        try {
          if (cid) {
            const single = await walletQuery(OVERDRAFT_FACILITY_QUERY, { companyId: cid });
            if (single?.overdraftFacility) {
              loadedFacilities = [single.overdraftFacility];
            }
          } else if (displayTenants.length > 0) {
            const results = await Promise.allSettled(
              displayTenants.map((t) => {
                const targetCid = parseInt(t.id ?? t.companyId, 10);
                return walletQuery(OVERDRAFT_FACILITY_QUERY, { companyId: targetCid });
              })
            );
            loadedFacilities = results
              .filter((r) => r.status === "fulfilled" && r.value?.overdraftFacility)
              .map((r) => r.value.overdraftFacility);
          }
        } catch (fbErr) {
          console.warn("[OverdraftFacility] Fallback query failed:", fbErr.message);
        }
      }
      setFacilities(loadedFacilities);

      // 2. Fetch Requests (maker-checker pending/approved/rejected requests)
      let loadedRequests = [];
      try {
        const reqRes = await walletQuery(OVERDRAFT_FACILITY_REQUESTS_QUERY, {
          companyId: cid,
          status: statusFilter,
        });
        if (Array.isArray(reqRes?.overdraftFacilityRequests)) {
          loadedRequests = reqRes.overdraftFacilityRequests;
        }
      } catch (reqErr) {
        console.warn("[OverdraftFacility] Requests query unavailable on server:", reqErr.message);
      }
      setRequests(loadedRequests);
    } catch (err) {
      console.error("[OverdraftFacility] Load error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedStatus, displayTenants]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Shared SweetAlert z-index helper (keeps OTP dialog above MUI dialogs)
  const swalOnTop = {
    customClass: { container: "swal-on-top" },
    didOpen: () => {
      const el = document.querySelector(".swal2-container");
      if (el) el.style.zIndex = "2000";
    },
  };

  // Handle Request Overdraft Facility (Maker step) — with OTP gate
  const handleCreateRequest = async () => {
    if (!targetCompanyId) {
      Swal.fire("Validation Error", "Please select a target tenant company.", "warning");
      return;
    }
    const limitNum = parseFloat(requestedLimit);
    if (!limitNum || limitNum <= 0) {
      Swal.fire("Validation Error", "Please enter a valid requested facility limit.", "warning");
      return;
    }
    const rateNum = parseFloat(annualRate);
    if (isNaN(rateNum) || rateNum < 0) {
      Swal.fire("Validation Error", "Please enter a valid annual interest rate percentage.", "warning");
      return;
    }
    const shareNum = parseFloat(investorShare || "0");
    if (isNaN(shareNum) || shareNum < 0 || shareNum > 100) {
      Swal.fire("Validation Error", "Investor share percentage must be between 0% and 100%.", "warning");
      return;
    }

    setRequesting(true);

    try {
      // ── Step 1: Request OTP ──────────────────────────────────────────
      Swal.fire({
        title: "Sending OTP...",
        text: "Please wait while your verification code is being generated.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = "2000";
        },
      });

      const otpRequest = await walletMutate(REQUEST_OVERDRAFT_MAKER_OTP_MUTATION);
      const otpData = otpRequest?.requestOverdraftMakerOtp;

      if (otpData?.error) {
        const errMsg = (() => {
          try { return JSON.parse(otpData.error)?.message || otpData.error; }
          catch { return otpData.error || "Failed to send OTP."; }
        })();
        Swal.fire({ icon: "error", title: "OTP Error", text: errMsg, ...swalOnTop });
        setRequesting(false);
        return;
      }

      let maskedEmail = "";
      try {
        const parsed = typeof otpData?.results === "string" ? JSON.parse(otpData.results) : otpData?.results;
        maskedEmail = parsed?.maskedEmail || "";
      } catch (err) {
        console.warn("Could not parse maskedEmail:", err);
      }

      const emailNotice = maskedEmail
        ? `An OTP has been sent to your registered email (${maskedEmail}).`
        : "An OTP has been sent to your registered email address.";

      // ── Step 2: Loop — prompt for OTP, allow resend ─────────────────
      let otpVerified = false;
      while (!otpVerified) {
        const otpPrompt = await Swal.fire({
          title: "Enter Overdraft Request OTP",
          html: `
            <div style="font-size: 14px; color: #555; margin-bottom: 15px;">
              ${emailNotice} Enter it below to proceed.
            </div>
          `,
          icon: "info",
          input: "text",
          inputPlaceholder: "Enter 6-digit OTP",
          inputAttributes: {
            maxlength: "6",
            autocapitalize: "off",
            autocorrect: "off",
            style: "text-align: center; font-size: 20px; letter-spacing: 4px; font-weight: bold;",
          },
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Verify & Submit",
          confirmButtonColor: "#5c67f5",
          cancelButtonText: "Cancel",
          denyButtonText: "Resend OTP",
          denyButtonColor: "#757575",
          inputValidator: (value) => {
            if (!value) return "OTP is required.";
            if (!/^\d{6}$/.test(value.trim())) return "OTP must be a 6-digit number.";
          },
          ...swalOnTop,
        });

        if (otpPrompt.isDenied) {
          // Resend OTP
          Swal.fire({
            title: "Resending OTP...",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
              const el = document.querySelector(".swal2-container");
              if (el) el.style.zIndex = "2000";
            },
          });
          const resendRes = await walletMutate(REQUEST_OVERDRAFT_MAKER_OTP_MUTATION);
          const resendData = resendRes?.requestOverdraftMakerOtp;
          if (resendData?.error) {
            const errM = (() => {
              try { return JSON.parse(resendData.error)?.message || resendData.error; }
              catch { return resendData.error || "Failed to resend OTP."; }
            })();
            await Swal.fire({ icon: "error", title: "Resend Failed", text: errM, ...swalOnTop });
          } else {
            await Swal.fire({
              icon: "success",
              title: "OTP Resent",
              text: "A new OTP has been dispatched to your email.",
              timer: 2000,
              showConfirmButton: false,
              ...swalOnTop,
            });
          }
          continue;
        }

        if (!otpPrompt.isConfirmed) {
          setRequesting(false);
          return;
        }

        const otp = otpPrompt.value?.trim();

        // ── Step 3: Validate OTP ───────────────────────────────────────
        Swal.fire({
          title: "Verifying OTP...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
            const el = document.querySelector(".swal2-container");
            if (el) el.style.zIndex = "2000";
          },
        });

        const validateRes = await walletMutate(VALIDATE_OVERDRAFT_MAKER_OTP_MUTATION, { otp });
        const validateData = validateRes?.validateOverdraftMakerOtp;

        if (validateData?.error) {
          const errMsg = (() => {
            try { return JSON.parse(validateData.error)?.message || validateData.error; }
            catch { return validateData.error || "Invalid OTP code entered."; }
          })();
          await Swal.fire({ icon: "error", title: "OTP Verification Failed", text: errMsg, ...swalOnTop });
          continue;
        }

        otpVerified = true;
      }

      // ── Step 4: Submit overdraft facility request ────────────────────
      Swal.fire({
        title: "Submitting Request...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = "2000";
        },
      });

      const input = {
        companyId: parseInt(targetCompanyId, 10),
        requestedLimit: limitNum,
        annualInterestRatePct: rateNum,
        investorSharePct: shareNum,
        investorReference: investorReference.trim() || `INV-OD-${targetCompanyId}-${new Date().getFullYear()}`,
        notes: notes.trim() || null,
      };

      const res = await walletMutate(REQUEST_OVERDRAFT_FACILITY_MUTATION, { input });
      const newId = res?.requestOverdraftFacility;

      Swal.fire({
        icon: "success",
        title: "Overdraft Facility Requested",
        text: `Request #${newId} for ${getCompanyName(targetCompanyId)} (Limit: ${formatKES(limitNum)}, ${rateNum}% p.m.) submitted. Awaiting checker approval.`,
      });

      setRequestOpen(false);
      setRequestedLimit("500000");
      setAnnualRate("12.0");
      setInvestorShare("80.0");
      setInvestorReference("");
      setNotes("");
      loadData();
    } catch (err) {
      console.error("[OverdraftFacility] Request error:", err);
      Swal.fire("Request Failed", err.message || "Failed to create overdraft facility request.", "error");
    } finally {
      setRequesting(false);
    }
  };

  // Handle Approve Overdraft Facility (Checker step) — with OTP
  const handleApproveRequest = async (requestId, companyId, limit, requestedBy) => {
    // Step 0 — Maker-Checker validation before proceeding to confirmation or OTP
    let currentUserId = 0;
    try {
      const decoded = JSON.parse(localStorage.getItem("decodedToken") || "{}");
      currentUserId = Number(decoded.Id || decoded.UserId || decoded.id || 0);
    } catch { }

    const creatorId = Number(requestedBy || 0);
    if (currentUserId > 0 && creatorId > 0 && currentUserId === creatorId) {
      Swal.fire({
        icon: "error",
        title: "Maker–Checker Violation",
        text: "You cannot approve an overdraft facility request that you created.",
      });
      return;
    }

    const compName = getCompanyName(companyId);

    // Step 1 — confirm intent
    const result = await Swal.fire({
      title: "Approve Overdraft Facility?",
      html: `Approve Overdraft Facility Request <b>#${requestId}</b> for <b>${compName}</b> (${formatKES(limit)})?<br/><small style="opacity:.7">Maker ≠ Checker enforced. An OTP will be sent to your email.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#22bf72",
      confirmButtonText: "Send OTP & Approve",
    });
    if (!result.isConfirmed) return;

    // Step 2 — request OTP (sent to approver's email)
    try {
      await walletMutate(REQUEST_APPROVAL_OTP_MUTATION, {
        actionType: "OVERDRAFT_APPROVAL",
        entityId: Number(requestId),
      });
    } catch (err) {
      const isMakerChecker = (err.message || "").toLowerCase().includes("maker-checker");
      Swal.fire({
        icon: "error",
        title: isMakerChecker ? "Maker–Checker Violation" : "OTP Error",
        text: err.message || "Failed to send OTP.",
      });
      return;
    }

    // Step 3 — prompt approver to enter OTP with 30-second cooldown
    const maskedEmail = getMaskedUserEmail();
    const emailNotice = maskedEmail
      ? `An OTP has been sent to <b>${maskedEmail}</b>.`
      : `An OTP has been sent to your registered email.`;

    let timerInterval = null;
    let secondsLeft = 30;

    const otpResult = await Swal.fire({
      icon: "info",
      title: "Enter Approval OTP",
      html: `
        <div style="font-size:0.95rem;color:#475569;margin-bottom:12px;">
          ${emailNotice}<br/>Enter the OTP to activate the overdraft facility.
        </div>
        <input id="swal-od-otp-input" class="swal2-input" placeholder="6-digit OTP" maxlength="10" autocomplete="one-time-code" style="text-align:center;letter-spacing:6px;font-size:1.3rem;font-weight:700;margin:10px auto;" />
        <div style="margin-top:12px;font-size:0.875rem;">
          <span id="od-otp-timer-box" style="color:#64748b;">
            Request another OTP in <b id="od-otp-countdown" style="color:#0284c7;">30</b>s
          </span>
          <button id="swal-od-resend-btn" type="button" style="display:none;background:none;border:none;color:#1f76ad;font-weight:700;cursor:pointer;text-decoration:underline;font-size:0.9rem;">
            Resend OTP
          </button>
        </div>
        <div id="od-otp-status-msg" style="margin-top:8px;font-size:0.82rem;min-height:18px;"></div>
      `,
      showCancelButton: true,
      confirmButtonText: "Verify & Activate",
      confirmButtonColor: "#22bf72",
      focusConfirm: false,
      didOpen: () => {
        const input = document.getElementById("swal-od-otp-input");
        if (input) input.focus();

        const timerBox = document.getElementById("od-otp-timer-box");
        const countdownEl = document.getElementById("od-otp-countdown");
        const resendBtn = document.getElementById("swal-od-resend-btn");
        const statusMsg = document.getElementById("od-otp-status-msg");

        const updateTimer = () => {
          if (secondsLeft > 0) {
            secondsLeft -= 1;
            if (countdownEl) countdownEl.innerText = String(secondsLeft);
          }
          if (secondsLeft <= 0) {
            clearInterval(timerInterval);
            if (timerBox) timerBox.style.display = "none";
            if (resendBtn) resendBtn.style.display = "inline-block";
          }
        };

        timerInterval = setInterval(updateTimer, 1000);

        if (resendBtn) {
          resendBtn.onclick = async () => {
            resendBtn.disabled = true;
            resendBtn.style.opacity = "0.5";
            if (statusMsg) {
              statusMsg.style.color = "#0284c7";
              statusMsg.innerText = "Requesting new OTP...";
            }
            try {
              await walletMutate(REQUEST_APPROVAL_OTP_MUTATION, {
                actionType: "OVERDRAFT_APPROVAL",
                entityId: parseInt(requestId, 10),
              });
              secondsLeft = 30;
              if (countdownEl) countdownEl.innerText = "30";
              if (timerBox) timerBox.style.display = "inline";
              if (resendBtn) resendBtn.style.display = "none";
              resendBtn.disabled = false;
              resendBtn.style.opacity = "1";
              if (statusMsg) {
                statusMsg.style.color = "#16a34a";
                statusMsg.innerText = "New OTP sent to your email!";
              }
              clearInterval(timerInterval);
              timerInterval = setInterval(updateTimer, 1000);
            } catch (err) {
              resendBtn.disabled = false;
              resendBtn.style.opacity = "1";
              if (statusMsg) {
                statusMsg.style.color = "#dc2626";
                statusMsg.innerText = err.message || "Failed to resend OTP.";
              }
            }
          };
        }
      },
      willClose: () => {
        if (timerInterval) clearInterval(timerInterval);
      },
      preConfirm: () => {
        const val = document.getElementById("swal-od-otp-input")?.value?.trim();
        if (!val) {
          Swal.showValidationMessage("OTP is required");
          return false;
        }
        return val;
      },
    });
    if (!otpResult.isConfirmed || !otpResult.value) return;

    try {
      await walletMutate(APPROVE_OVERDRAFT_FACILITY_MUTATION, {
        requestId: parseInt(requestId, 10),
        otp: otpResult.value.trim(),
      });
      Swal.fire("Approved & Activated", "The overdraft facility is now ACTIVE.", "success");
      loadData();
    } catch (err) {
      Swal.fire("Approval Failed", err.message || "Maker-checker policy rejected approval.", "error");
    }
  };

  // Handle Suspend Overdraft Facility
  const handleSuspendFacility = async (companyId) => {
    const compName = getCompanyName(companyId);
    const result = await Swal.fire({
      title: "Suspend Overdraft Facility?",
      text: `Suspend overdraft draws for ${compName}? No new overdraft disbursements can occur until reactivated.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ed6c02",
      confirmButtonText: "Suspend Facility",
    });

    if (result.isConfirmed) {
      try {
        await walletMutate(SUSPEND_OVERDRAFT_FACILITY_MUTATION, { companyId: parseInt(companyId, 10) });
        Swal.fire("Facility Suspended", `${compName} overdraft line is now SUSPENDED.`, "success");
        loadData();
      } catch (err) {
        Swal.fire("Suspension Failed", err.message || "Failed to suspend facility.", "error");
      }
    }
  };

  // Handle Close Overdraft Facility
  const handleCloseFacility = async (companyId) => {
    const compName = getCompanyName(companyId);
    const result = await Swal.fire({
      title: "Permanently Close Facility?",
      text: `Permanently close the overdraft facility for ${compName}? This cannot be undone.`,
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      confirmButtonText: "Close Facility",
    });

    if (result.isConfirmed) {
      try {
        await walletMutate(CLOSE_OVERDRAFT_FACILITY_MUTATION, { companyId: parseInt(companyId, 10) });
        Swal.fire("Facility Closed", `${compName} overdraft line is now CLOSED.`, "success");
        loadData();
      } catch (err) {
        Swal.fire("Close Failed", err.message || "Failed to close overdraft facility.", "error");
      }
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "ACTIVE":
        return <Chip label="ACTIVE" color="success" size="small" sx={{ fontWeight: 700 }} />;
      case "PENDING_APPROVAL":
        return <Chip label="PENDING APPROVAL" color="warning" size="small" sx={{ fontWeight: 700 }} />;
      case "APPROVED":
        return <Chip label="APPROVED" color="success" size="small" sx={{ fontWeight: 700 }} />;
      case "SUSPENDED":
        return <Chip label="SUSPENDED" color="warning" size="small" sx={{ fontWeight: 700 }} />;
      case "CLOSED":
        return <Chip label="CLOSED" color="default" size="small" sx={{ fontWeight: 700 }} />;
      case "REJECTED":
        return <Chip label="REJECTED" color="error" size="small" sx={{ fontWeight: 700 }} />;
      default:
        return <Chip label={status || "UNKNOWN"} size="small" />;
    }
  };

  // KPI Calculations
  const activeCount = facilities.filter((f) => f.status === "ACTIVE").length;
  const totalLimit = facilities
    .filter((f) => f.status === "ACTIVE")
    .reduce((sum, f) => sum + (parseFloat(f.facilityLimit) || 0), 0);
  const totalDrawn = facilities.reduce((sum, f) => sum + (parseFloat(f.outstandingPrincipal) || 0), 0);
  const totalInterest = facilities.reduce((sum, f) => sum + (parseFloat(f.accruedInterest) || 0), 0);

  return (
    <Box sx={{ width: "100%" }}>
      {/* ── Top Summary Header Card ────────────────────────────────────────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          background: isDark
            ? "linear-gradient(135deg, rgba(23,102,162,0.18) 0%, rgba(156,39,176,0.15) 100%)"
            : "linear-gradient(135deg, #f0f7ff 0%, #faf5ff 100%)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <CreditCardOutlined sx={{ color: "#1766A2", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                  Investor Overdraft Facilities & Credit Line Governance
                </Typography>
                <Chip
                  icon={<CheckCircleOutline fontSize="small" />}
                  label={`${activeCount} Active Facilities`}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              <Typography variant="body2" sx={{ color: colors.grey[400], maxWidth: 880 }}>
                Investor-backed overdraft facilities provide guaranteed liquidity buffers for tenant MFI disbursements.
                Daily interest accrues automatically on outstanding drawn balances with double-entry GL journal posting.
                Maker-checker governance enforces that the facility requester and approver are distinct officers.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshOutlined />}
                onClick={loadData}
                sx={{
                  borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                  color: colors.grey[100],
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2,
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined />}
                onClick={() => setRequestOpen(true)}
                sx={{
                  backgroundColor: "#1766A2",
                  "&:hover": { backgroundColor: "#125182" },
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2.5,
                  borderRadius: 2,
                }}
              >
                Request Overdraft Facility
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* ── KPI Summary Cards ──────────────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  ACTIVE FACILITIES
                </Typography>
                <LayersOutlined sx={{ color: "#1766A2", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1766A2" }}>
                {activeCount}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Out of {facilities.length} total facilities
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  TOTAL CREDIT LIMIT
                </Typography>
                <CreditCardOutlined sx={{ color: "#22bf72", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#22bf72" }}>
                {formatKES(totalLimit)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Approved available facility pool
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  OUTSTANDING PRINCIPAL
                </Typography>
                <TrendingUpOutlined sx={{ color: "#ed6c02", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#ed6c02" }}>
                {formatKES(totalDrawn)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Currently drawn across tenants
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  ACCRUED INTEREST
                </Typography>
                <AccountBalanceOutlined sx={{ color: "#9c27b0", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#9c27b0" }}>
                {formatKES(totalInterest)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Pending waterfall repayment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              select
              size="small"
              label="Filter by Tenant Institution"
              value={selectedCompanyId}
              onChange={(e) => {
                setSelectedCompanyId(e.target.value);
                setRequestsPage(0);
                setFacilitiesPage(0);
              }}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">All Tenant Institutions</MenuItem>
              {displayTenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} (#{t.id})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Lifecycle Status"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setRequestsPage(0);
                setFacilitiesPage(0);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </TextField>

            <Tooltip title="Refresh overdraft records">
              <IconButton size="small" onClick={loadData}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Table 1: Overdraft Facility Requests & Approval Lifecycle ───────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], mb: 0.5 }}>
            Overdraft Facility Requests & Approval Lifecycle
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mb: 2 }}>
            Maker-checker governance: DRAFT &rarr; PENDING_APPROVAL &rarr; ACTIVE (approver &ne; creator).
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : requests.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No overdraft facility requests found. Click &quot;Request Overdraft Facility&quot; to initiate a request.
              </Typography>
            </Box>
          ) : (<>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Req ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tenant Company</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#22bf72" }}>Requested Limit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Interest Rate</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Investor Share</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Investor Reference</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Approved By</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests
                    .slice(
                      requestsPage * requestsRowsPerPage,
                      requestsPage * requestsRowsPerPage + requestsRowsPerPage
                    )
                    .map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>#{row.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getCompanyName(row.companyId)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: "#22bf72" }}>
                          {formatKES(row.requestedLimit)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.annualInterestRatePct}% p.m.
                        </TableCell>
                        <TableCell>{row.investorSharePct ?? 0}%</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {row.investorReference || "—"}
                          </Typography>
                          {row.notes && (
                            <Typography variant="caption" sx={{ color: colors.grey[400], display: "block" }}>
                              {row.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{getStatusChip(row.status)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                            {getUserDisplayName(row.requestedBy, userMap)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                            {row.createdDate || row.requestedDate
                              ? new Date(row.createdDate || row.requestedDate).toLocaleDateString()
                              : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.approvedBy ? (
                            <>
                              <Typography variant="caption" sx={{ display: "block", color: "#22bf72", fontWeight: 700 }}>
                                {getUserDisplayName(row.approvedBy, userMap)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                {row.approvedDate ? new Date(row.approvedDate).toLocaleDateString() : ""}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                              Pending
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.status === "PENDING_APPROVAL" ? (
                            <Tooltip title="Approve Facility (Checker)">
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<VerifiedUserOutlined />}
                                onClick={() => handleApproveRequest(row.id, row.companyId, row.requestedLimit, row.requestedBy)}
                                sx={{ textTransform: "none", fontWeight: 700 }}
                              >
                                Approve
                              </Button>
                            </Tooltip>
                          ) : (
                            <Chip label={row.status} size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={requests.length}
              page={requestsPage}
              onPageChange={(_, newPage) => setRequestsPage(newPage)}
              rowsPerPage={requestsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setRequestsRowsPerPage(parseInt(e.target.value, 10));
                setRequestsPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                  }`,
              }}
            />
          </>)}
        </CardContent>
      </Card>

      {/* ── Table 2: Active / Configured Overdraft Facilities & Controls ────── */}
      <Card
        sx={{
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], mb: 0.5 }}>
            Configured Tenant Overdraft Facilities & Operating Status
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mb: 2 }}>
            Active overdraft facilities backing tenant disbursements. Administrators can suspend or close credit lines.
          </Typography>

          {facilities.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No active overdraft facilities configured. Approve a pending request above to activate a facility.
              </Typography>
            </Box>
          ) : (<>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Facility ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tenant Company</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#22bf72" }}>Credit Limit</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#ed6c02" }}>Outstanding Drawn</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#9c27b0" }}>Accrued Interest</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Annual Rate</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Investor Share</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Facility Controls</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facilities
                    .slice(
                      facilitiesPage * facilitiesRowsPerPage,
                      facilitiesPage * facilitiesRowsPerPage + facilitiesRowsPerPage
                    )
                    .map((fac) => (
                      <TableRow key={fac.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>#{fac.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getCompanyName(fac.companyId)}
                          </Typography>
                          {fac.investorReference && (
                            <Typography variant="caption" sx={{ color: colors.grey[400], display: "block" }}>
                              Ref: {fac.investorReference}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: "#22bf72" }}>
                          {formatKES(fac.facilityLimit)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: fac.outstandingPrincipal > 0 ? "#ed6c02" : colors.grey[300] }}>
                          {formatKES(fac.outstandingPrincipal || 0)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: fac.accruedInterest > 0 ? "#9c27b0" : colors.grey[300] }}>
                          {formatKES(fac.accruedInterest || 0)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {fac.annualInterestRatePct}% p.m.
                        </TableCell>
                        <TableCell>{fac.investorSharePct ?? 0}%</TableCell>
                        <TableCell>{getStatusChip(fac.status)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {fac.status === "ACTIVE" && (
                              <Tooltip title="Suspend Facility (Pause Draws)">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={<PauseCircleOutline />}
                                  onClick={() => handleSuspendFacility(fac.companyId)}
                                  sx={{ textTransform: "none", fontWeight: 700 }}
                                >
                                  Suspend
                                </Button>
                              </Tooltip>
                            )}

                            {fac.status !== "CLOSED" && (
                              <Tooltip title="Permanently Close Facility">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<BlockOutlined />}
                                  onClick={() => handleCloseFacility(fac.companyId)}
                                  sx={{ textTransform: "none", fontWeight: 700 }}
                                >
                                  Close
                                </Button>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={facilities.length}
              page={facilitiesPage}
              onPageChange={(_, newPage) => setFacilitiesPage(newPage)}
              rowsPerPage={facilitiesRowsPerPage}
              onRowsPerPageChange={(e) => {
                setFacilitiesRowsPerPage(parseInt(e.target.value, 10));
                setFacilitiesPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                  }`,
              }}
            />
          </>)}
        </CardContent>
      </Card>

      {/* ── Dialog: Request Overdraft Facility (Maker step) ─────────────────── */}
      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Request Overdraft Facility
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mb: 3 }}>
            Create an overdraft facility request for an MFI tenant. Upon submission, the request enters
            PENDING_APPROVAL state awaiting a distinct checker officer approval.
          </Typography>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Target Tenant Institution *"
                value={targetCompanyId}
                onChange={(e) => setTargetCompanyId(e.target.value)}
              >
                {displayTenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} (#{t.id})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Requested Limit (KES) *"
                value={requestedLimit}
                onChange={(e) => setRequestedLimit(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Monthly Interest Rate (%) *"
                value={annualRate}
                onChange={(e) => setAnnualRate(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">% p.m.</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Investor Share (%) *"
                value={investorShare}
                onChange={(e) => setInvestorShare(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Investor Reference"
                placeholder="e.g. INV-CREDIT-2026"
                value={investorReference}
                onChange={(e) => setInvestorReference(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Notes / Governance Justification"
                placeholder="e.g. Liquidity buffer for agricultural loan disbursements"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRequestOpen(false)} sx={{ textTransform: "none", color: colors.grey[300] }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRequest}
            disabled={requesting}
            startIcon={requesting ? <CircularProgress size={16} color="inherit" /> : <SendOutlined />}
            sx={{
              backgroundColor: "#1766A2",
              "&:hover": { backgroundColor: "#125182" },
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              px: 3,
            }}
          >
            {requesting ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OverdraftFacilityManagement;
